/** Tenant guest seats with optional sponsor filter (TMMPDM-0008). */
import { useEffect, useState } from 'react';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import type { AdminSeatRow } from '@/api/tmm/types';
import { formatDate } from '@/format';
import { StatusChip } from '@/status';

interface SeatsPanelProps {
  slug: string;
  currency: string;
}

export function SeatsPanel({ slug }: SeatsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<AdminSeatRow[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);
  const [sponsorInput, setSponsorInput] = useState('');

  const sponsorId = sponsorInput.trim() === '' ? undefined : Number(sponsorInput);
  const sponsorQuery =
    sponsorId !== undefined && Number.isInteger(sponsorId) ? sponsorId : undefined;

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listSeats(slug, sponsorQuery !== undefined ? { sponsorUserId: sponsorQuery } : undefined)
      .then((list) => {
        if (!cancelled) {
          setRows(list.seats);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, sponsorQuery, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('seats.loadError', 'Could not load seats.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <Stack spacing={2}>
      <TextField
        label={t('seats.sponsorFilter', 'Sponsor user id')}
        value={sponsorInput}
        onChange={(event) => setSponsorInput(event.target.value)}
        type="number"
      />
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={t('seats.tableAria', 'Tenant seats')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('seats.col.guest', 'Guest')}</TableCell>
              <TableCell>{t('seats.col.sponsor', 'Sponsor')}</TableCell>
              <TableCell>{t('seats.col.status', 'Status')}</TableCell>
              <TableCell>{t('seats.col.since', 'Since')}</TableCell>
              <TableCell>{t('seats.col.ends', 'Ends')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t('common.loading', 'Loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <EmptyState title={t('seats.empty', 'No guest seats.')} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                return (
                  <TableRow key={row.entitlementId}>
                    <TableCell>{row.guestDisplayName ?? String(row.userId)}</TableCell>
                    <TableCell>{row.sponsorDisplayName ?? String(row.sponsorUserId)}</TableCell>
                    <TableCell>
                      <StatusChip
                        status={row.status}
                        label={t(`seats.status.${row.status}`, row.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.since, locale)}</TableCell>
                    <TableCell>{formatDate(row.endsAt, locale)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
